import React, { Component } from "react";
import gql from "graphql-tag";
import { Query, Mutation } from "react-apollo";
import { Link, withRouter } from "react-router-dom";
import { EditorState } from "draft-js";
import { toHTML, fromHTML } from "../../../utils/customDraftConverters";

import {
  isValidUri,
  isValidImageSize,
  getImageDimensions
} from "utils/helpers";
import { contentTypeOptions } from "config/constants";
import { CONTENTS_QUERY } from "../List";
import { TAGS_QUERY, CATEGORIES_QUERY } from "graphql/queries";

import { Grid, Form, Button, Header, Segment } from "semantic-ui-react";
import ContentEditor from "./Editor";
import { GraphQLError } from "components/GraphQLError";
import { withApplicationId, withLanguage } from "HOCs"

const SourceTable = ({ sources, deleteSource }) => {
  return (
    <table className="bordered">
      <thead>
        <tr>
          <th>Source Name</th>
          <th>Source URL</th>
        </tr>
      </thead>
      <tbody>
        {sources.map(source => (
          <tr key={source.name}>
            <td>{source.name}</td>
            <td>
              <a target="_blank" href={source.url}>
                {source.url}
              </a>
            </td>
            <td style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                className="btn red lighten-1"
                onClick={() => deleteSource(source.name)}
              >
                Delete
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

class FormCommons extends Component {
  state = {
    template: "",
    title: "",
    description: "",
    videoUrl: "",
    categoryId: "",
    imgUrl: "",
    tags: [],
    sources: [],
    html: "",
    editorState: EditorState.createEmpty(),

    selectedTags: [],
    topImage: null,
    searchQuery: "",
    sourceName: "",
    sourceUrl: "",
    sourceValid: true
  };

  componentDidMount() {
    if (this.props.initialValues) {
      let {
        body,
        tags,
        sources,
        webUrl,
        html,
        categoryId,
        ...rest
      } = this.props.initialValues;

      this.setState({
        ...rest,
        tags: tags ? tags.map(tag => tag.id) : [],
        selectedTags: tags ? tags : [],
        sources: sources ? sources.map(({ name, url }) => ({ name, url })) : [],
        html,
        categoryId,
        editorState: EditorState.createWithContent(fromHTML(html))
      });
    }
  }

  handleCategoryChange = (event, { value }) => {
    this.setState({ categoryId: value });
  };

  handleImageFileChange = async event => {
    const input = event.target;
    const preview = document.querySelector(`#${event.target.name}-preview`);
    const file = input.files[0];

    const dimensions = await getImageDimensions(file);

    if (isValidImageSize(dimensions)) {
      const reader = new FileReader();

      reader.addEventListener("load", () => {
        preview.src = reader.result;
        this.setState({ imgUrl: reader.result });
      });

      if (file) {
        reader.readAsDataURL(file);
        this.setState({ files: file });
      }
    } else {
      preview.src = "";
      this.setState({ imgUrl: "" });
    }
  };

  handleTagChange = (event, { value, options }) => {
    this.setState({
      tags: value,
      selectedTags: value.map(id => {
        const tag = options.find(o => o.value === id);
        return {
          id: tag.value,
          name: tag.name
        };
      }),
      searchQuery: ""
    });
  };

  handleSearchChange = (event, { searchQuery }) => {
    this.setState({ searchQuery });
  };

  handleAddSource = event => {
    this.setState(state => {
      return {
        sources: state.sources.concat({
          name: state.sourceName,
          url: state.sourceUrl
        }),
        sourceName: "",
        sourceUrl: ""
      };
    });
  };

  handleDeleteSource = name => {
    this.setState(state => {
      return {
        sources: state.sources.filter(source => source.name !== name)
      };
    });
  };

  handleChange = (event, data) => {
    if (data.type === "url") {
      const sourceValid = isValidUri(data.value);
      this.setState({
        [event.target.id]: event.target.value,
        sourceValid
      });
    } else {
      this.setState({ [event.target.id]: event.target.value });
    }
  };

  handleEditorChange = editorState => {
    const html = toHTML(editorState.getCurrentContent());
    this.setState({
      editorState,
      html
    });
  };

  handleTemplateChange = (event, { value }) => {
    this.setState({ template: value });
  };

  renderSaveNewButton() {
    const { language, applicationId, languageQuery } = this.props;
    const {
      title,
      description,
      categoryId,
      template,
      selectedTags,
      sources,
      html,
      videoUrl,
      imgUrl,
      files
    } = this.state;
    return (
      <Mutation
        mutation={CREATE_CONTENT_MUTATION}
        refetchQueries={[
          {
            query: CONTENTS_QUERY,
            variables: { language, applicationId, limit: 20, offset: 0, q: "" },
            fetchPolicy: "network-only"
          }
        ]}
      >
        {(createContent, { loading, error }) => {
          return (
            <React.Fragment>
              {error && <GraphQLError error={error} />}
              <Button
                primary
                loading={loading}
                onClick={async () => {
                  await createContent({
                    variables: {
                      language,
                      input: {
                        title,
                        description,
                        applicationId,
                        categoryId,
                        template,
                        tags: JSON.stringify(selectedTags),
                        sources,
                        html,
                        videoUrl,
                        imgUrl
                      },
                      files
                    }
                  });
                  this.props.history.push(`/applications/${applicationId}/contents?${languageQuery}`);
                }}
              >
                {loading ? "Saving..." : "Save"}
              </Button>
            </React.Fragment>
          );
        }}
      </Mutation>
    );
  }

  renderSaveEditButton() {
    const {
      title,
      description,
      categoryId,
      template,
      selectedTags,
      sources,
      html,
      videoUrl,
      imgUrl,
      files
    } = this.state;
    const {
      match: { params },
      language,
      applicationId,
      languageQuery
    } = this.props;
    return (
      <Mutation
        mutation={UPDATE_CONTENT_MUTATION}
        refetchQueries={[
          {
            query: CONTENTS_QUERY,
            variables: { language, applicationId, limit: 20, offset: 0, q: "" },
            fetchPolicy: "network-only"
          }
        ]}
      >
        {(updateContent, { loading, error }) => {
          return (
            <React.Fragment>
              {error && <GraphQLError error={error} />}
              <Button
                primary
                loading={loading}
                onClick={async () => {
                  await updateContent({
                    variables: {
                      language,
                      id: params.contentId,
                      input: {
                        title,
                        description,
                        categoryId,
                        applicationId,
                        template,
                        videoUrl,
                        imgUrl,
                        tags: JSON.stringify(selectedTags),
                        sources,
                        html
                      },
                      files
                    }
                  });
                  this.props.history.push(`/applications/${applicationId}/contents?${languageQuery}`);
                }}
              >
                {loading ? "Updating..." : "Update"}
              </Button>
            </React.Fragment>
          );
        }}
      </Mutation>
    );
  }

  render() {
    const { initialValues, match, language, applicationId, languageQuery } = this.props;
    const {
      // content fields
      title,
      description,
      categoryId,
      template,
      sources,
      videoUrl,
      imgUrl,

      // helper fields
      tags,
      searchQuery,
      sourceName,
      sourceUrl,
      sourceValid
    } = this.state;

    const hasTopImage =
      template === "article" ||
      template === "listicle" ||
      template === "news" ||
      template === "infographic";
    const hasTopVideo = template === "video";
    const hasDescription = template === "card" || template === "news";
    const hasSources = template !== "video";
    // const hasPublishedTime = template === 'news'
    const disableFormType = !!(match && match.params && match.params.contentId);
    const disableAddSource = !sourceName || !sourceUrl;
    return (
      <React.Fragment>
        <Grid columns={2}>
          <Grid.Row>
            <Grid.Column>
              <Form.Dropdown
                placeholder="Select content type..."
                options={contentTypeOptions}
                value={this.state.template}
                onChange={this.handleTemplateChange}
                disabled={disableFormType}
                selection
              />
            </Grid.Column>

            <Grid.Column textAlign="right">
              {initialValues
                ? this.renderSaveEditButton()
                : this.renderSaveNewButton()}

              <Link to={`/applications/${applicationId}/contents?${languageQuery}`}>
                <Button negative>Cancel</Button>
              </Link>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {template ? (
          <Form>
            <Form.Group>
              <Query
                query={CATEGORIES_QUERY}
                variables={{ language, applicationId }}
                fetchPolicy="network-only"
              >
                {({ data, loading, error }) => {
                  return (
                    <Form.Dropdown
                      required
                      name="category"
                      label="Content Category"
                      selection
                      options={
                        data &&
                        data.categories &&
                        data.categories.map(category => {
                          return {
                            key: category.id,
                            value: category.id,
                            text: category.name
                          };
                        })
                      }
                      value={this.state.categoryId}
                      onChange={this.handleCategoryChange}
                      loading={loading}
                      error={!!error}
                    />
                  );
                }}
              </Query>
            </Form.Group>

            <Form.Group>
              <Form.Input
                required
                width={16}
                id="title"
                placeholder="Title"
                label="Title"
                onChange={this.handleChange}
                value={title}
              />
            </Form.Group>

            {hasDescription && (
              <Form.Group>
                <Form.TextArea
                  width={16}
                  id="description"
                  label="Description"
                  placeholder="Description"
                  onChange={this.handleChange}
                  value={description}
                />
              </Form.Group>
            )}

            {hasTopImage && (
              <Form.Group>
                <Form.Input
                  label={`${
                    template === "infographic" ? "Thumbnail" : "Main"
                  } Image Upload`}
                  name="imgUpload"
                  type="file"
                  accept=".jpg,.png,.jpeg,.gif,.svg"
                  onChange={this.handleImageFileChange}
                />
                <img
                  src={imgUrl || ""}
                  alt=""
                  className="responsive-img"
                  id="imgUpload-preview"
                />
              </Form.Group>
            )}

            {hasTopVideo && (
              <Form.Group>
                <Form.Input
                  width={16}
                  id="videoUrl"
                  label="Video URL"
                  type="url"
                  required
                  placeholder="https://www.youtube.com/embed/tRcPA7Fzebw"
                  value={videoUrl}
                  onChange={this.handleChange}
                />
                <Form.Input
                  width={16}
                  id="imgUrl"
                  label="Video Thumbnail URL"
                  type="url"
                  placeholder="https://img.youtube.com/vi/<videoID>/maxresdefault.jpg"
                  value={imgUrl}
                  onChange={this.handleChange}
                />
                <p>
                  Hint: For thumbnail, try <br />
                  <a href="">
                    {"https://img.youtube.com/vi/<videoID>/maxresdefault.jpg"}
                  </a>{" "}
                  <br /> or{" "}
                  <a href="">{"https://img.youtube.com/vi/<videoID>/0.jpg"}</a>{" "}
                  if the first doesn't exist.
                </p>
              </Form.Group>
            )}

            <Form.Group>
              <Query
                query={TAGS_QUERY}
                variables={{
                  language,
                  q: searchQuery,
                  limit: 20
                }}
                fetchPolicy="network-only"
              >
                {({ data, loading, error }) => {
                  if (!searchQuery && loading) {
                    return <div>loading...</div>;
                  }

                  if (error) {
                    return <div>error fetching tags</div>;
                  }
                  const selectedTags = this.state.selectedTags.map(t => ({
                    text: t.name,
                    name: t.name,
                    value: t.id
                  }));
                  const tagsQuery = data.tags || [];

                  return (
                    <Form.Select
                      width={16}
                      label="Tags"
                      name="Select tags..."
                      multiple
                      search
                      selection
                      value={tags}
                      options={tagsQuery
                        .map(t => ({
                          text: t.name,
                          name: t.name,
                          value: t.id
                        }))
                        .concat(selectedTags)}
                      searchQuery={searchQuery}
                      onChange={this.handleTagChange}
                      onSearchChange={this.handleSearchChange}
                    />
                  );
                }}
              </Query>
            </Form.Group>

            {hasSources && (
              <Form.Group>
                <Form.Input
                  width={6}
                  id="sourceName"
                  placeholder="Source name"
                  label="Source name"
                  value={sourceName}
                  onChange={this.handleChange}
                />
                <Form.Input
                  width={6}
                  id="sourceUrl"
                  placeholder="Source URL"
                  label="Source URL"
                  type="url"
                  value={sourceUrl}
                  onChange={this.handleChange}
                  error={!sourceValid}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-end"
                  }}
                >
                  <Button
                    width={4}
                    positive
                    content="Add Source"
                    onClick={this.handleAddSource}
                    disabled={disableAddSource}
                  />
                </div>
              </Form.Group>
            )}

            {hasSources ? (
              <div className="row">
                {sources.length ? (
                  <SourceTable
                    deleteSource={this.handleDeleteSource}
                    sources={sources}
                  />
                ) : null}
              </div>
            ) : null}

            <Header as="h3" content="Content Body" />
            <Segment>
              <ContentEditor
                editorState={this.state.editorState}
                handleEditorChange={this.handleEditorChange}
              />
            </Segment>
          </Form>
        ) : (
          <Header as="h1" content="Select a content type..." />
        )}
      </React.Fragment>
    );
  }
}

const CREATE_CONTENT_MUTATION = gql`
  mutation CreateContent(
    $language: String!
    $input: CreateContentInput!
    $files: Upload
  ) {
    createContent(language: $language, input: $input, files: $files) {
      id
      title
      template
      categoryId
      applicationId
    }
  }
`;

const UPDATE_CONTENT_MUTATION = gql`
  mutation UpdateContent(
    $language: String!
    $id: ID!
    $input: UpdateContentInput!
    $files: Upload
  ) {
    updateContent(language: $language, id: $id, input: $input, files: $files) {
      id
      title
      description
      imgUrl
      videoUrl
      template
      categoryId
      applicationId
      tags {
        id
        name
      }
      sources {
        name
        url
      }
      body {
        header
        text
        imgUrl
      }
    }
  }
`;

export default withRouter(withApplicationId(withLanguage(FormCommons)));



// WEBPACK FOOTER //
// ./src/pages/Contents/New/Form.js